import { useState, useEffect } from 'react';
import api from '@/api';
import { format, parseISO, isPast, isFuture } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, Clock, CheckCircle, XCircle, Hourglass, TrendingUp, UserX, UserMinus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Ожидает', icon: Hourglass, color: 'text-amber-400', bg: 'bg-amber-900/30 border-amber-900/50' },
  confirmed: { label: 'Подтверждена', icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-900/30 border-green-900/50' },
  cancelled: { label: 'Отменена', icon: XCircle, color: 'text-red-400', bg: 'bg-red-900/30 border-red-900/50' },
  no_show_patient: { label: 'Неявка клиента', icon: UserX, color: 'text-violet-400', bg: 'bg-violet-900/30 border-violet-900/50' },
  no_show_doctor: { label: 'Неявка психолога', icon: UserMinus, color: 'text-blue-400', bg: 'bg-blue-900/30 border-blue-900/50' },
};

export function PatientHistoryDialog({ open, onOpenChange, patient }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !patient) return;
    setLoading(true);
    api.get(`/patients/${patient.id}/appointments`)
      .then(({ data }) => setAppointments(data))
      .catch(() => setAppointments([]))
      .finally(() => setLoading(false));
  }, [open, patient]);

  const past = appointments.filter(a => a.status !== 'cancelled' && isPast(parseISO(`${a.date}T${a.time}`)));
  const upcoming = appointments.filter(a => a.status !== 'cancelled' && isFuture(parseISO(`${a.date}T${a.time}`)));
  const cancelled = appointments.filter(a => a.status === 'cancelled');
  const totalHours = past.reduce((sum, a) => sum + (a.duration || 60) / 60, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading text-stone-200" data-testid="history-dialog-title">
            История: {patient?.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-stone-500 text-center py-8">Загрузка...</p>
        ) : appointments.length === 0 ? (
          <p className="text-stone-600 text-center py-8">Записей не найдено</p>
        ) : (
          <div className="space-y-5 overflow-y-auto pr-1 flex-1">
            {/* Stats */}
            <div data-testid="history-stats" className="grid grid-cols-3 gap-3">
              <div className="bg-stone-950/50 border border-stone-800 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-500">{past.length}</p>
                <p className="text-[10px] text-stone-500 uppercase tracking-wider mt-1">Визитов</p>
              </div>
              <div className="bg-stone-950/50 border border-stone-800 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-400">{upcoming.length}</p>
                <p className="text-[10px] text-stone-500 uppercase tracking-wider mt-1">Предстоит</p>
              </div>
              <div className="bg-stone-950/50 border border-stone-800 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-stone-300">{totalHours.toFixed(1)}</p>
                <p className="text-[10px] text-stone-500 uppercase tracking-wider mt-1">Часов</p>
              </div>
            </div>

            {/* Upcoming */}
            {upcoming.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <TrendingUp size={12} /> Предстоящие
                </h4>
                <div className="space-y-2">
                  {upcoming.map(a => (
                    <TimelineItem key={a.id} appt={a} />
                  ))}
                </div>
              </div>
            )}

            {/* Past */}
            {past.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Clock size={12} /> Прошлые визиты
                </h4>
                <div className="space-y-2">
                  {past.map(a => (
                    <TimelineItem key={a.id} appt={a} />
                  ))}
                </div>
              </div>
            )}

            {/* Cancelled */}
            {cancelled.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-red-400/70 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <XCircle size={12} /> Отменённые ({cancelled.length})
                </h4>
                <div className="space-y-2">
                  {cancelled.map(a => (
                    <TimelineItem key={a.id} appt={a} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TimelineItem({ appt }) {
  const cfg = statusConfig[appt.status] || statusConfig.pending;
  const Icon = cfg.icon;
  let dateStr;
  try { dateStr = format(parseISO(appt.date), 'd MMM yyyy', { locale: ru }); } catch { dateStr = appt.date; }

  return (
    <div data-testid={`history-item-${appt.id}`} className={`flex items-start gap-3 p-3 rounded-lg border ${cfg.bg} bg-stone-950/30`}>
      <div className={`mt-0.5 ${cfg.color}`}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-stone-200 text-sm font-medium truncate">{appt.format_name}</p>
          <Badge className={`${cfg.bg} ${cfg.color} text-[10px] border shrink-0`}>{cfg.label}</Badge>
        </div>
        <p className="text-stone-400 text-xs mt-0.5 flex items-center gap-1.5">
          <CalendarDays size={11} /> {dateStr} в {appt.time}
          <span className="text-stone-600">&middot;</span>
          {appt.duration} мин
        </p>
        {appt.notes && <p className="text-stone-600 text-xs mt-1 truncate">{appt.notes}</p>}
      </div>
    </div>
  );
}
